import {
  Component,
  signal,
  OnInit,
  OnDestroy,
  computed,
  inject,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { rxResource } from '@angular/core/rxjs-interop';
import { PopupService } from '@api-client';
import { SwiperDirective } from '@client-libs';
import type { SwiperOptions } from 'swiper/types';

@Component({
  selector: 'app-popup-banner',
  imports: [CommonModule, SwiperDirective],
  templateUrl: './popup-banner.component.html',
  styles: `
    .popup-enter {
      animation: popupIn 300ms ease-out forwards;
    }
    @keyframes popupIn {
      from {
        transform: scale(0.9);
        opacity: 0;
      }
      to {
        transform: scale(1);
        opacity: 1;
      }
    }
  `,
})
export class PopupBannerComponent implements OnInit, OnDestroy {
  private readonly popupService = inject(PopupService);

  private dismissedPopups = signal<Set<string>>(new Set());

  $popups = rxResource({
    stream: () => this.popupService.popupControllerFindAll(),
  });

  visiblePopups = computed(() => {
    const popups = this.$popups.value() || [];
    const dismissed = this.dismissedPopups();
    return popups.filter(
      popup => !dismissed.has(popup.id) && !this.isHiddenForToday(popup.id)
    );
  });

  mobilePopupSwiperOptions: SwiperOptions = {
    slidesPerView: 1,
    spaceBetween: 16,
    loop: false,
    autoplay: {
      delay: 3000,
      disableOnInteraction: false,
    },
    pagination: {
      el: '.swiper-pagination',
      clickable: true,
    },
  };

  private scrollLockEffect = effect(() => {
    if (this.visiblePopups().length > 0) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  });

  ngOnInit(): void {
    this.loadDismissedPopups();
  }

  ngOnDestroy(): void {
    document.body.style.overflow = '';
  }

  private loadDismissedPopups(): void {
    const today = this.getTodayString();
    const dismissed = new Set<string>();

    const storedData = localStorage.getItem('popup_dismissed');
    if (storedData) {
      try {
        const parsed = JSON.parse(storedData);
        if (parsed.date === today) {
          parsed.ids.forEach((id: string) => dismissed.add(id));
        } else {
          localStorage.removeItem('popup_dismissed');
        }
      } catch {
        localStorage.removeItem('popup_dismissed');
      }
    }

    this.dismissedPopups.set(dismissed);
  }

  private isHiddenForToday(popupId: string): boolean {
    const today = this.getTodayString();
    const storedData = localStorage.getItem('popup_dismissed');

    if (storedData) {
      try {
        const parsed = JSON.parse(storedData);
        if (parsed.date === today) {
          return parsed.ids.includes(popupId);
        }
      } catch {
        return false;
      }
    }

    return false;
  }

  closePopup(popupId: string): void {
    const dismissed = new Set(this.dismissedPopups());
    dismissed.add(popupId);
    this.dismissedPopups.set(dismissed);
  }

  closeAllPopups(): void {
    const allPopupIds = this.visiblePopups().map(p => p.id);
    const dismissed = new Set(this.dismissedPopups());
    allPopupIds.forEach(id => dismissed.add(id));
    this.dismissedPopups.set(dismissed);
  }

  hideForToday(popupId: string): void {
    const today = this.getTodayString();

    let storedData = { date: today, ids: [] as string[] };
    const existing = localStorage.getItem('popup_dismissed');

    if (existing) {
      try {
        const parsed = JSON.parse(existing);
        if (parsed.date === today) {
          storedData = parsed;
        }
      } catch {
        // ignore parse errors
      }
    }

    if (!storedData.ids.includes(popupId)) {
      storedData.ids.push(popupId);
    }

    localStorage.setItem('popup_dismissed', JSON.stringify(storedData));

    this.closePopup(popupId);
  }

  hideAllForToday(): void {
    const allPopupIds = this.visiblePopups().map(p => p.id);
    allPopupIds.forEach(id => this.hideForToday(id));
  }

  private getTodayString(): string {
    return new Date().toISOString().split('T')[0];
  }
}
